import * as React from 'react';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import { classes } from './CoreTag.st.css';
const SPACE_KEY = 32;
export const noop = () => null;
export class CoreFocusableTag extends React.Component {
    constructor() {
        super(...arguments);
        this.inputRef = React.createRef();
        this.labelRef = React.createRef();
        this.anchorRef = React.createRef();
        this.handleKeyDown = (ev) => {
            const { link } = this.props;
            /**
             * By default, list item can't be selected by Space button,
             * so we call click on input.
             * Also, keyCode is 100% cross-browser - number 32 is everywhere
             */
            if (ev.keyCode === SPACE_KEY) {
                ev.preventDefault();
                this.inputRef.current.click();
                /**
                 * We need to preserve focus on selected-deselected element,
                 * so we need to set focus on wrapper, when chain of events
                 * click -> change -> blur will be fired
                 */
                const PAUSE_FOR_BLUR_EVENT = 10;
                setTimeout(() => {
                    if (link) {
                        this.anchorRef.current.focus();
                    }
                    else {
                        this.labelRef.current.focus();
                    }
                }, PAUSE_FOR_BLUR_EVENT);
            }
        };
        this.handleTagClick = (ev) => {
            ev.preventDefault();
            /**
             * Label wrapped in anchor, so we simulate click on input
             * to fire change event and onChange listener.
             */
            this.inputRef.current.click();
        };
        this.renderLabeledInput = (ownProps, wrapperProps = {}) => {
            const { value, label, disabled, checked, onChange, compId, children, uniqueId, tabIndex, } = ownProps;
            return (React.createElement("label", { ref: this.labelRef, "data-hook": "tags-list-tag" /* DataHooks.Tag */, title: label, htmlFor: uniqueId, tabIndex: tabIndex, ...wrapperProps },
                React.createElement("input", { ref: this.inputRef, "data-hook": "tags-list-tag-input" /* DataHooks.TagInput */, className: classes.tagInput, type: "checkbox", checked: checked, onChange: onChange, value: value, name: compId || label, id: uniqueId, disabled: disabled }),
                children));
        };
    }
    render() {
        const { value = '', label = '', disabled, checked, link, rel, onChange = noop, compId, tagIndex, children, className, 
        /**
         * component props may contain non-deterministically-named attribute
         * created by "stylable" and used as css-selector.
         * This variable will keep it and forward to root element.
         */
        ...wrapperStyles } = this.props;
        const uniqueId = tagIndex !== undefined
            ? `${value}-${compId}-${tagIndex}`
            : `${value}-${compId}`;
        const isFocusDisabled = Boolean(disabled || link);
        const tabIndex = isFocusDisabled ? -1 : 0;
        const rootElementProps = {
            onKeyDown: this.handleKeyDown,
            className: classNames(classes.tag, className),
            ...wrapperStyles,
        };
        const labeledInputProps = {
            value,
            label,
            disabled,
            checked,
            onChange,
            tabIndex,
            uniqueId,
            compId,
            children,
        };
        return link ? (React.createElement("a", { ref: this.anchorRef, role: "checkbox", "aria-checked": checked, "aria-disabled": disabled, href: link, rel: rel, tabIndex: disabled ? -1 : 0, onClick: this.handleTagClick, ...rootElementProps }, this.renderLabeledInput(labeledInputProps))) : (this.renderLabeledInput(labeledInputProps, rootElementProps));
    }
}
CoreFocusableTag.displayName = "CoreTag" /* DisplayNames.Tag */;
CoreFocusableTag.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    checked: PropTypes.bool,
    value: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
    link: PropTypes.string,
    rel: PropTypes.string,
    compId: PropTypes.string,
    tagIndex: PropTypes.number,
    style: PropTypes.object,
};
export class CoreTag extends React.Component {
    constructor() {
        super(...arguments);
        this.inputRef = React.createRef();
        this.labelRef = React.createRef();
        this.anchorRef = React.createRef();
        this.handleKeyDown = (ev) => {
            /**
             * By default, list item can't be selected by Space button,
             * so we call click on input.
             * Also, keyCode is 100% cross-browser - number 32 is everywhere
             */
            if (ev.keyCode === SPACE_KEY) {
                ev.preventDefault();
                this.inputRef.current.click();
            }
        };
        this.handleTagClick = (ev) => {
            ev.preventDefault();
            /**
             * Label wrapped in anchor, so we simulate click on input
             * to fire change event and onChange listener.
             */
            this.inputRef.current.click();
        };
        this.renderLabeledInput = (props, labelProps = {}) => {
            const { value, label, disabled, checked, onChange, compId, children, uniqueId, tabIndex, } = props;
            return (React.createElement("div", { "data-hook": "tags-list-tag" /* DataHooks.Tag */, className: classes.tagContainer },
                React.createElement("input", { ref: this.inputRef, "data-hook": "tags-list-tag-input" /* DataHooks.TagInput */, className: classes.tagInput, type: "checkbox", checked: checked, onChange: onChange, value: value, name: compId || label, id: uniqueId, disabled: disabled }),
                React.createElement("label", { ref: this.labelRef, "data-hook": "tags-list-tag-label" /* DataHooks.TagLabel */, title: label, htmlFor: uniqueId, tabIndex: tabIndex, role: "checkbox", "aria-checked": checked, "aria-disabled": disabled, ...labelProps }, children)));
        };
    }
    render() {
        const { children, className, value = '', label = '', disabled, checked, onChange = noop, link, rel, compId, tagIndex, ...rest } = this.props;
        const uniqueId = tagIndex !== undefined
            ? `${value}-${compId}-${tagIndex}`
            : `${value}-${compId}`;
        const isFocusDisabled = Boolean(disabled || link);
        const tabIndex = isFocusDisabled ? -1 : 0;
        const wrapperProps = {
            className: classNames(classes.tag, className),
            onKeyDown: this.handleKeyDown,
            ...rest,
        };
        const labeledInputProps = {
            value,
            label,
            disabled,
            checked,
            onChange,
            uniqueId,
            compId,
            children,
            tabIndex,
        };
        return link ? (React.createElement("a", { ref: this.anchorRef, role: "checkbox", "aria-checked": checked, "aria-disabled": disabled, href: link, rel: rel, tabIndex: disabled ? -1 : 0, onClick: this.handleTagClick, ...wrapperProps }, this.renderLabeledInput(labeledInputProps))) : (this.renderLabeledInput(labeledInputProps, wrapperProps));
    }
}
CoreTag.displayName = "CoreTag" /* DisplayNames.Tag */;
CoreTag.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    checked: PropTypes.bool,
    value: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
    link: PropTypes.string,
    rel: PropTypes.string,
    compId: PropTypes.string,
    tagIndex: PropTypes.number,
};
//# sourceMappingURL=CoreTag.js.map